import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ColorLens as ColorIcon
} from '@mui/icons-material';
import { ChromePicker } from 'react-color';

const GridTagManager = ({ open, onClose, tags, onSave }) => {
  const theme = useTheme();
  const [localTags, setLocalTags] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [newTag, setNewTag] = useState({ name: '', color: '#1976d2', description: '' });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerTarget, setColorPickerTarget] = useState(null);

  useEffect(() => {
    setLocalTags(tags || []);
  }, [tags]);

  const handleAddTag = () => {
    if (newTag.name.trim()) {
      const tag = {
        _id: `new-${Date.now()}`,
        ...newTag,
        name: newTag.name.trim()
      };
      setLocalTags([...localTags, tag]);
      setNewTag({ name: '', color: '#1976d2', description: '' });
    }
  };

  const handleUpdateTag = (tagId, updates) => {
    setLocalTags(localTags.map(tag => 
      tag._id === tagId ? { ...tag, ...updates } : tag
    ));
  };

  const handleDeleteTag = (tagId) => {
    setLocalTags(localTags.filter(tag => tag._id !== tagId));
  };

  const handleColorChange = (color) => {
    if (colorPickerTarget === 'new') {
      setNewTag({ ...newTag, color: color.hex });
    } else {
      handleUpdateTag(colorPickerTarget, { color: color.hex });
    }
  };

  const handleSave = () => {
    // Remove temporary IDs from new tags before saving
    const tagsToSave = localTags.map(tag => {
      if (tag._id && tag._id.toString().startsWith('new-')) {
        // Remove the temporary ID for new tags
        const { _id, ...tagWithoutId } = tag;
        return tagWithoutId;
      }
      return tag;
    });
    onSave(tagsToSave);
  };

  const openColorPicker = (target) => {
    setColorPickerTarget(target);
    setShowColorPicker(true);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Manage Grid Tags</Typography>
        <Typography variant="caption" color="textSecondary">
          Create tags to categorize your social media posts
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Add new tag */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Add New Tag
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Tag name"
              value={newTag.name}
              onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => openColorPicker('new')}
              sx={{
                backgroundColor: newTag.color,
                color: theme.palette.getContrastText(newTag.color),
                '&:hover': {
                  backgroundColor: alpha(newTag.color, 0.8)
                }
              }}
            >
              <ColorIcon fontSize="small" />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddTag}
              disabled={!newTag.name.trim()}
            >
              Add
            </Button>
          </Box>
          <TextField
            size="small"
            placeholder="Description (optional)"
            value={newTag.description}
            onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Existing tags */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Existing Tags
        </Typography>
        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {localTags.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary="No tags yet"
                secondary="Add tags to categorize your grid posts"
              />
            </ListItem>
          ) : (
            localTags.map((tag) => (
              <ListItem key={tag._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Chip
                    label={tag.name}
                    size="small"
                    sx={{
                      backgroundColor: tag.color,
                      color: theme.palette.getContrastText(tag.color),
                      mr: 2
                    }}
                  />
                  {editingTag === tag._id ? (
                    <TextField
                      size="small"
                      value={tag.name}
                      onChange={(e) => handleUpdateTag(tag._id, { name: e.target.value })}
                      onBlur={() => setEditingTag(null)}
                      onKeyPress={(e) => e.key === 'Enter' && setEditingTag(null)}
                      autoFocus
                    />
                  ) : (
                    <ListItemText
                      primary={tag.name}
                      secondary={tag.description}
                    />
                  )}
                </Box>
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => openColorPicker(tag._id)}
                  >
                    <ColorIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setEditingTag(tag._id)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDeleteTag(tag._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>

        {/* Color Picker Popover */}
        {showColorPicker && (
          <>
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 1299,
                backgroundColor: 'rgba(0, 0, 0, 0.1)'
              }}
              onClick={() => setShowColorPicker(false)}
            />
            <Box
              sx={{
                position: 'fixed',
                zIndex: 1300,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <ChromePicker
                color={colorPickerTarget === 'new' 
                  ? newTag.color 
                  : localTags.find(t => t._id === colorPickerTarget)?.color
                }
                onChange={handleColorChange}
                onChangeComplete={() => {
                  // Optional: close on color selection
                  // setShowColorPicker(false);
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          magical={true}
        >
          Save Tags
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GridTagManager;